/**
 * F-306 — Texas property-card sections (human directive 2026-09-28)
 *
 * Replaces the UK-oriented blocks on PropertyDetail for the Texas market:
 *  - PRS Registration        -> RentalRegistrationCard + RekeyStatusCard
 *  - Certificate tracker     -> HvacReadinessCard
 *  - NLP Compliance analysis -> TexasDocumentValidation
 *  - (menu-only Analytics/Score) -> TexasPropertyInsights element on the card,
 *    feeding the all-properties summary already on the Texas dashboard.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import texasApi from '../../../services/texasApi';
import { complianceService } from '../../../services/api';
import { getCertificateTypesSync, saveCertificateTypes } from '../../../services/certificateTypes';
import PRSWizard from './PRSWizard';

type Res<T> = { data?: any; [k: string]: any };

const unwrap = <T,>(res: Res<T> | null | undefined): any => {
    if (!res) return null;
    return res.data?.data ?? res.data ?? res;
};

/* ───────────────────────── Rental Registrations ───────────────────────── */

export const RentalRegistrationCard: React.FC<{ propertyId: string }> = ({ propertyId }) => {
    const [status, setStatus] = useState<any>(null);
    const [loaded, setLoaded] = useState(false);
    // F-307: the Texas Registration wizard is launchable from inside each
    // property (human directive: "Texas Registration everywhere").
    const [showWizard, setShowWizard] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await texasApi.dallasRegistration.getStatus(propertyId);
                if (alive) setStatus(unwrap(res));
            } catch {
                if (alive) setStatus(null);
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [propertyId]);

    const st = String(status?.status || status?.registrationStatus || '').toLowerCase();
    const registered = Boolean(status?.registrationNumber || status?.registrationNo || st === 'active' || st === 'registered');
    const expiring = st === 'expiring' || st === 'expiring_soon';
    const expired = st === 'expired';

    const badge = !loaded
        ? <span className="text-xs text-gray-400">Loading…</span>
        : expired
            ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800"><i className="fas fa-exclamation-triangle mr-1"></i>Expired</span>
            : expiring
                ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800"><i className="fas fa-clock mr-1"></i>Expiring Soon</span>
                : registered
                    ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-800"><i className="fas fa-check-circle mr-1"></i>Registered</span>
                    : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800"><i className="fas fa-times-circle mr-1"></i>Not Registered</span>;

    return (
        <div className="bg-white shadow rounded-lg p-6" data-testid="texas-rental-registration-card">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                    <i className="fas fa-clipboard-list text-indigo-500 mr-2"></i>
                    Rental Registrations
                </h3>
                {badge}
            </div>
            {loaded && registered && (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                        <dt className="text-gray-500">Registration Number</dt>
                        <dd className="font-semibold text-gray-900">{status?.registrationNumber || status?.registrationNo || '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-gray-500">Renewal</dt>
                        <dd className="font-semibold text-gray-900">{status?.expiryDate || status?.renewalDate || status?.nextRenewal || '—'}</dd>
                    </div>
                </dl>
            )}
            {loaded && !registered && (
                <p className="text-sm text-gray-500">No municipal registration on file for this property.</p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-4">
                <Link to="/texas/registrations" className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800">
                    Manage registrations <i className="fas fa-arrow-right ml-1 text-xs"></i>
                </Link>
                <button
                    type="button"
                    onClick={() => setShowWizard(true)}
                    className="inline-flex items-center text-sm font-semibold text-emerald-600 hover:text-emerald-800"
                >
                    <i className="fas fa-clipboard-check mr-1.5"></i> Texas Registration Wizard
                </button>
            </div>
            <PRSWizard isOpen={showWizard} onClose={() => setShowWizard(false)} />
        </div>
    );
};

/* ──────────────────────────── 7-Day Re-Key ────────────────────────────── */

export const RekeyStatusCard: React.FC<{ propertyId: string }> = ({ propertyId }) => {
    const [ledger, setLedger] = useState<any>(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await texasApi.rekeyLedger.getLedger(propertyId);
                if (alive) setLedger(unwrap(res));
            } catch {
                if (alive) setLedger(null);
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [propertyId]);

    const units: any[] = Array.isArray(ledger) ? ledger : (ledger?.units || ledger?.entries || ledger?.rekeys || []);
    const nonCompliant = units.filter((u) => {
        const s = String(u?.status || '').toLowerCase();
        return s && s !== 'compliant' && s !== 'ok' && s !== 'pass';
    }).length;

    return (
        <div className="bg-white shadow rounded-lg p-6" data-testid="texas-rekey-card">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                    <i className="fas fa-door-closed text-indigo-500 mr-2"></i>
                    7-Day Re-Key
                </h3>
                {!loaded ? (
                    <span className="text-xs text-gray-400">Loading…</span>
                ) : units.length === 0 ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-600">No Records</span>
                ) : nonCompliant === 0 ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-800"><i className="fas fa-check-circle mr-1"></i>Compliant</span>
                ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800"><i className="fas fa-exclamation-triangle mr-1"></i>Action Required</span>
                )}
            </div>
            <p className="text-sm text-gray-500">
                {units.length > 0
                    ? `${units.length} re-key record${units.length === 1 ? '' : 's'} tracked${nonCompliant > 0 ? ` — ${nonCompliant} need attention` : ''}.`
                    : 'Track key re-issuance within 7 days of a tenant change (Tex. Prop. Code § 92.165).'}
            </p>
            <Link to="/texas/rekey" className="mt-4 inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800">
                Open re-key ledger <i className="fas fa-arrow-right ml-1 text-xs"></i>
            </Link>
        </div>
    );
};

/* ─────────────────────────── HVAC Readiness ───────────────────────────── */

export const HvacReadinessCard: React.FC<{ propertyId: string }> = ({ propertyId }) => {
    const [hvac, setHvac] = useState<any>(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await texasApi.hvacReadiness.getProperty(propertyId);
                if (alive) setHvac(unwrap(res));
            } catch {
                if (alive) setHvac(null);
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [propertyId]);

    const st = String(hvac?.status || '').toLowerCase();
    const ready = st === 'ready' || st === 'compliant' || hvac?.summerTestCompleted === true;

    return (
        <div className="bg-white shadow rounded-lg p-6" data-testid="texas-hvac-card">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                    <i className="fas fa-snowflake text-sky-500 mr-2"></i>
                    HVAC Readiness
                </h3>
                {!loaded ? (
                    <span className="text-xs text-gray-400">Loading…</span>
                ) : ready ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-800"><i className="fas fa-check-circle mr-1"></i>Ready</span>
                ) : hvac ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800"><i className="fas fa-clock mr-1"></i>In Progress</span>
                ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-600">Not Started</span>
                )}
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                    <dt className="text-gray-500">Summer Test</dt>
                    <dd className="font-semibold text-gray-900">{hvac?.summerTestDate || (hvac?.summerTestCompleted ? 'Completed' : '—')}</dd>
                </div>
                <div>
                    <dt className="text-gray-500">Winter Checklist</dt>
                    <dd className="font-semibold text-gray-900">{hvac?.winterChecklistDate || (hvac?.winterChecklistCompleted ? 'Completed' : '—')}</dd>
                </div>
            </dl>
            <p className="text-xs text-gray-400 mt-3">Tex. Prop. Code § 92.251 — A/C must be operational when outdoor temp exceeds 95°F.</p>
            <Link to="/texas/hvac" className="mt-3 inline-flex items-center text-sm font-semibold text-sky-600 hover:text-sky-800">
                Open HVAC readiness <i className="fas fa-arrow-right ml-1 text-xs"></i>
            </Link>
        </div>
    );
};

/* ───────────────────── Texas Document Validation ──────────────────────── */

/**
 * F-307 — Digital vault document slots.
 *
 * Up to 5 slots drawn from the tenant's US certificate-type settings (the
 * same list CertificateList and the add-property upload step use). Each slot
 * is user-editable (label + required flag, persisted via the certificate-
 * types settings API), shows VALID/EXPIRED/MISSING/N-A against the stored
 * certificates, and offers an inline presigned upload (the UK "Add
 * Certificate" flow, adapted — never removed). The valid-count score is
 * surfaced on TexasPropertyInsights.
 */

interface DocSlot {
    id: string;
    key: string;
    label: string;
    defaultLabel: string;
    required: boolean;
}

const TEXAS_DOC_DEFAULTS: DocSlot[] = [
    { id: 'lease-agreement', key: 'Lease Agreement', label: 'Texas Lease Agreement', defaultLabel: 'Texas Lease Agreement', required: true },
    { id: 'lead-paint-disclosure', key: 'Lead Paint Disclosure', label: 'Lead Paint Disclosure (pre-1978 homes)', defaultLabel: 'Lead Paint Disclosure (pre-1978 homes)', required: false },
    { id: 'registration-certificate', key: 'Registration Certificate', label: 'Municipal Registration Certificate', defaultLabel: 'Municipal Registration Certificate', required: true },
    { id: 'hvac-service', key: 'HVAC Service Record', label: 'HVAC / A/C Service Record', defaultLabel: 'HVAC / A/C Service Record', required: true },
    { id: 'condition-report', key: 'Condition Report', label: 'Property Condition Report', defaultLabel: 'Property Condition Report', required: false },
];

export const computeDocSlots = (yearBuilt?: number | string): DocSlot[] => {
    const types = getCertificateTypesSync() || [];
    const slots = TEXAS_DOC_DEFAULTS.map((def) => {
        const t = types.find((x: any) => String(x?.id || '') === def.id)
            || types.find((x: any) => String(x?.key || '').toLowerCase() === def.key.toLowerCase())
            || types.find((x: any) => String(x?.label || '').toLowerCase() === def.defaultLabel.toLowerCase())
            || types.find((x: any) => String(x?.key || '').toLowerCase() === def.defaultLabel.toLowerCase());
        return {
            ...def,
            label: String(t?.label || t?.key || def.label),
            required: typeof t?.required === 'boolean' ? t.required : def.required,
        };
    });
    // Lead Paint Disclosure is required only for pre-1978 homes.
    const pre1978 = Number(yearBuilt) > 0 && Number(yearBuilt) < 1978;
    if (pre1978) slots[1].required = true;
    return slots;
};

export const findDocCert = (certs: any[], slot: DocSlot) =>
    certs.find((c) => {
        const t = String(c?.type || '').toLowerCase();
        const n = String(c?.name || '').toLowerCase();
        return t === slot.key.toLowerCase() || n === slot.key.toLowerCase()
            || n === slot.defaultLabel.toLowerCase() || t === slot.defaultLabel.toLowerCase();
    });

const docIsExpired = (cert: any) => {
    if (!cert?.expiryDate && !cert?.expires) return false;
    return new Date(cert.expiryDate || cert.expires).getTime() < Date.now();
};

export const TexasDocumentValidation: React.FC<{
    propertyId?: string;
    certificates?: any[];
    yearBuilt?: number | string;
    onUploaded?: () => void;
}> = ({ propertyId, certificates = [], yearBuilt, onUploaded }) => {
    const certs = Array.isArray(certificates) ? certificates : [];
    const [slotOverride, setSlotOverride] = useState<Record<string, Partial<DocSlot>>>({});
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [draftLabel, setDraftLabel] = useState('');
    const [pendingType, setPendingType] = useState<string | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [pendingExpiry, setPendingExpiry] = useState('');
    const [busy, setBusy] = useState(false);
    const [uploadErr, setUploadErr] = useState('');

    const baseSlots = computeDocSlots(yearBuilt);
    const slots = baseSlots.map((s) => ({ ...s, ...(slotOverride[s.key] || {}) }));

    const evaluate = (slot: DocSlot) => {
        const cert = findDocCert(certs, slot);
        const present = Boolean(cert);
        const expired = present && docIsExpired(cert);
        const valid = present && !expired;
        const na = !slot.required && !present;
        return { cert, present, expired, valid, na };
    };

    const evaluated = slots.map((s) => ({ slot: s, ...evaluate(s) }));
    const active = evaluated.filter((e) => !e.na);
    const validCount = active.filter((e) => e.valid).length;
    const missingRequired = evaluated.filter((e) => e.slot.required && !e.valid);

    const persistSlot = async (slot: DocSlot, patch: Partial<DocSlot>) => {
        setSlotOverride((prev) => ({ ...prev, [slot.key]: { ...prev[slot.key], ...patch } }));
        try {
            const types = getCertificateTypesSync();
            const next = types.map((t: any) => {
                const match = String(t?.id || '') === slot.id
                    || String(t?.key || '').toLowerCase() === slot.key.toLowerCase()
                    || String(t?.key || '').toLowerCase() === slot.defaultLabel.toLowerCase()
                    || String(t?.label || '').toLowerCase() === slot.defaultLabel.toLowerCase();
                if (!match) return t;
                return {
                    ...t,
                    ...(patch.label ? { key: patch.label, label: patch.label } : {}),
                    ...(patch.required !== undefined ? { required: patch.required } : {}),
                };
            });
            await saveCertificateTypes(next);
        } catch {
            /* label kept in session state even if the settings save fails */
        }
    };

    const startEdit = (slot: DocSlot) => { setEditingKey(slot.key); setDraftLabel(slot.label); };
    const commitEdit = async (slot: DocSlot) => {
        if (draftLabel.trim() && draftLabel.trim() !== slot.label) {
            await persistSlot(slot, { label: draftLabel.trim() });
        }
        setEditingKey(null);
    };

    const handleFilePicked = (slot: DocSlot, file: File | null) => {
        if (!file) return;
        setUploadErr('');
        setPendingType(slot.key);
        setPendingFile(file);
        setPendingExpiry('');
    };

    const confirmUpload = async () => {
        if (!propertyId || !pendingType || !pendingFile) return;
        if (!pendingExpiry) { setUploadErr('Expiry date is required'); return; }
        const slot = slots.find((s) => s.key === pendingType);
        if (!slot) return;
        setBusy(true);
        setUploadErr('');
        try {
            const expiry = new Date(pendingExpiry);
            const isExpired = expiry.getTime() < Date.now();
            const presign = await complianceService.getPresignedUploadUrl({
                fileName: pendingFile.name,
                fileType: pendingFile.type || 'application/octet-stream',
                propertyId,
            });
            const { uploadUrl, s3Key } = presign.data?.data || {};
            if (!uploadUrl || !s3Key) throw new Error('Could not obtain upload URL');
            const putRes = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': pendingFile.type || 'application/octet-stream' },
                body: pendingFile,
            });
            if (!putRes.ok) throw new Error(`S3 upload failed (${putRes.status})`);
            await complianceService.confirmCertificateUpload({
                name: slot.label,
                type: slot.key,
                propertyId,
                issuedDate: new Date().toISOString().split('T')[0],
                expiryDate: pendingExpiry,
                s3Key,
                fileName: pendingFile.name,
                fileSize: pendingFile.size,
                uploadTimestamp: new Date().toISOString(),
                isExpired,
            });
            setPendingType(null);
            setPendingFile(null);
            setPendingExpiry('');
            onUploaded?.();
        } catch (e: any) {
            setUploadErr(e?.response?.data?.message || e?.message || 'Upload failed. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white shadow rounded-lg p-6" data-testid="texas-document-validation">
            <div className="flex justify-between items-center mb-1">
                <h3 className="text-lg font-medium text-gray-900">
                    <i className="fas fa-file-check text-emerald-600 mr-2"></i>
                    Texas Document Validation
                </h3>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${missingRequired.length === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                    {missingRequired.length === 0 ? `${validCount}/${active.length} valid` : `${missingRequired.length} required missing`}
                </span>
            </div>
            <p className="text-xs text-gray-400 mb-4">
                Digital vault — upload, label and mark required documents for this property (up to 5).
            </p>
            <ul className="space-y-2" role="list">
                {evaluated.map(({ slot, valid, expired, na }) => (
                    <li key={slot.key} className="p-2.5 bg-gray-50 rounded-lg" data-testid={`doc-slot-${slot.key}`}>
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-gray-800 min-w-0">
                                {editingKey === slot.key ? (
                                    <span className="flex items-center gap-1.5">
                                        <input
                                            className="text-sm border border-gray-300 rounded px-2 py-0.5 w-full"
                                            value={draftLabel}
                                            autoFocus
                                            onChange={(e) => setDraftLabel(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(slot); if (e.key === 'Escape') setEditingKey(null); }}
                                            aria-label="Document label"
                                        />
                                        <button type="button" className="text-emerald-600 font-bold text-xs" onClick={() => commitEdit(slot)} aria-label="Save label">
                                            <i className="fas fa-check"></i>
                                        </button>
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2 min-w-0">
                                        <span className="truncate">{slot.label}</span>
                                        <button
                                            type="button"
                                            className="text-gray-400 hover:text-indigo-600 flex-shrink-0"
                                            onClick={() => startEdit(slot)}
                                            title="Edit label"
                                            aria-label={`Edit ${slot.label}`}
                                        >
                                            <i className="fas fa-pen text-[10px]"></i>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => persistSlot(slot, { required: !slot.required })}
                                            title={slot.required ? 'Required — click to make optional' : 'Optional — click to make required'}
                                            aria-label={`Toggle required for ${slot.label}`}
                                            className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider flex-shrink-0 ${slot.required ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-500'}`}
                                        >
                                            {slot.required ? 'Required' : 'Optional'}
                                        </button>
                                    </span>
                                )}
                            </span>
                            <span className="flex-shrink-0">
                                {na ? (
                                    <span className="text-[10px] font-bold text-gray-400">N/A</span>
                                ) : valid ? (
                                    <span className="text-xs font-bold text-emerald-700"><i className="fas fa-check-circle mr-1"></i>VALID</span>
                                ) : expired ? (
                                    <span className="text-xs font-bold text-red-700"><i className="fas fa-times-circle mr-1"></i>EXPIRED</span>
                                ) : (
                                    <span className="text-xs font-bold text-red-700"><i className="fas fa-times-circle mr-1"></i>MISSING</span>
                                )}
                            </span>
                        </div>

                        {/* Inline upload — the adapted Add Certificate flow */}
                        {!valid && !na && propertyId && (
                            pendingType === slot.key ? (
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs bg-white border border-gray-200 rounded-lg p-2">
                                    <span className="font-semibold text-gray-700 truncate max-w-[10rem]">{pendingFile?.name}</span>
                                    <input
                                        type="date"
                                        className="border border-gray-300 rounded px-2 py-1"
                                        value={pendingExpiry}
                                        onChange={(e) => setPendingExpiry(e.target.value)}
                                        aria-label="Expiry date"
                                    />
                                    <button type="button" onClick={confirmUpload} disabled={busy || !pendingExpiry} className="px-3 py-1 bg-emerald-600 text-white rounded font-bold disabled:opacity-50">
                                        {busy ? <i className="fas fa-spinner fa-spin"></i> : 'Save to Vault'}
                                    </button>
                                    <button type="button" onClick={() => { setPendingType(null); setPendingFile(null); setUploadErr(''); }} className="px-2 py-1 text-gray-500 font-bold">Cancel</button>
                                </div>
                            ) : (
                                <label className="mt-2 inline-flex items-center cursor-pointer text-xs font-bold text-indigo-600 hover:text-indigo-800">
                                    <i className="fas fa-cloud-upload-alt mr-1.5"></i> Upload document
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => handleFilePicked(slot, e.target.files?.[0] || null)}
                                    />
                                </label>
                            )
                        )}
                        {uploadErr && pendingType === slot.key && (
                            <p className="mt-1 text-xs text-red-600">{uploadErr}</p>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

/* ───────────────── Texas Portfolio Insights (Analytics + Score) ───────── */

/**
 * F-307 — Texas Insights also carries the logic of the removed Litigation
 * Readiness card (Critical Docs / Syntax & Wording / Investigatory / Admin
 * Hygiene pillars + total score, values synced live via property.health) and
 * reports the digital-vault document score from TexasDocumentValidation.
 */
export const TexasPropertyInsights: React.FC<{
    propertyId: string;
    health?: any;
    certificates?: any[];
    yearBuilt?: number | string;
}> = ({ propertyId, health, certificates = [], yearBuilt }) => {
    const [portfolio, setPortfolio] = useState<any>(null);
    const [score, setScore] = useState<any>(null);
    const [loaded, setLoaded] = useState(false);

    const load = useCallback(async () => {
        const [pf, sc] = await Promise.allSettled([
            texasApi.complianceDashboard.getPortfolio(),
            texasApi.securityScore.getProperty(propertyId),
        ]);
        if (pf.status === 'fulfilled') setPortfolio(pf.value);
        if (sc.status === 'fulfilled') setScore(sc.value);
        setLoaded(true);
    }, [propertyId]);

    useEffect(() => { load(); }, [load]);

    const scorePct = score?.overallMax
        ? Math.round((score.overallScore / score.overallMax) * 100)
        : null;

    // Digital-vault doc score — same evaluation as TexasDocumentValidation.
    const slots = computeDocSlots(yearBuilt);
    const docsActive = slots.filter((s) => {
        const cert = findDocCert(Array.isArray(certificates) ? certificates : [], s);
        return s.required || Boolean(cert);
    });
    const docsValid = docsActive.filter((s) => {
        const cert = findDocCert(Array.isArray(certificates) ? certificates : [], s);
        return cert && !docIsExpired(cert);
    }).length;

    const pillars = health?.pillars
        ? [
            { title: 'Critical Docs', pillar: health.pillars.criticalDocs, icon: 'fa-file-medical' },
            { title: 'Syntax & Wording', pillar: health.pillars.syntaxWording, icon: 'fa-paragraph' },
            { title: 'Investigatory', pillar: health.pillars.investigatory, icon: 'fa-search' },
            { title: 'Admin Hygiene', pillar: health.pillars.adminHygiene, icon: 'fa-clipboard-check' },
        ].filter((p) => p.pillar)
        : [];

    const statusColor = health
        ? (health.totalScore >= 90 ? 'text-green-600'
            : health.totalScore >= 70 ? 'text-blue-600'
                : health.totalScore >= 50 ? 'text-yellow-600' : 'text-red-600')
        : 'text-slate-900';

    return (
        <div className="bg-white shadow rounded-lg p-6" data-testid="texas-property-insights">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                    <i className="fas fa-chart-bar text-indigo-500 mr-2"></i>
                    Texas Insights
                </h3>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Portfolio summary feeds the dashboard</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-[10px] font-bold text-slate-500 tracking-wider">PROPERTIES</div>
                    <div className="text-2xl font-black text-slate-900 mt-0.5">{loaded ? (portfolio?.propertyCount ?? '—') : '…'}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-[10px] font-bold text-emerald-600 tracking-wider">SCORE</div>
                    <div className="text-2xl font-black text-emerald-600 mt-0.5">{loaded ? (portfolio?.score ?? '—') : '…'}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-[10px] font-bold text-amber-600 tracking-wider">EXPIRING</div>
                    <div className="text-2xl font-black text-amber-600 mt-0.5">{loaded ? (portfolio?.expiringCount ?? '—') : '…'}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-[10px] font-bold text-red-600 tracking-wider">EXPIRED</div>
                    <div className="text-2xl font-black text-red-600 mt-0.5">{loaded ? (portfolio?.expiredCount ?? '—') : '…'}</div>
                </div>
            </div>

            {/* F-307: digital-vault document score (Texas Document Validation) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-xl p-3 col-span-2 sm:col-span-1" data-testid="insights-docs">
                    <div className="text-[10px] font-bold text-indigo-600 tracking-wider">DOCUMENTS</div>
                    <div className="text-2xl font-black text-indigo-600 mt-0.5">
                        {docsValid}/{docsActive.length}
                    </div>
                </div>
                {health && (
                    <div className="bg-gray-50 rounded-xl p-3 col-span-2 sm:col-span-3" data-testid="insights-litigation">
                        <div className="text-[10px] font-bold text-slate-500 tracking-wider">LITIGATION READINESS (SYNCED LIVE)</div>
                        <div className={`text-2xl font-black mt-0.5 ${statusColor}`}>
                            {health.totalScore}%
                            <span className="ml-2 text-xs font-semibold text-gray-400">{health.status}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 tracking-wider">THIS PROPERTY — SECURITY &amp; SAFETY SCORE</div>
                        <div className="text-3xl font-black text-slate-900 mt-1">
                            {scorePct !== null ? `${scorePct}%` : loaded ? '—' : '…'}
                            {scorePct !== null && (
                                <span className="ml-2 text-xs font-semibold text-gray-400">({score.overallScore}/{score.overallMax})</span>
                            )}
                        </div>
                    </div>
                    <i className="fas fa-shield-halved text-2xl text-emerald-500"></i>
                </div>
                {Array.isArray(score?.pillars) && score.pillars.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                        {score.pillars.slice(0, 4).map((p: any) => {
                            const pct = p.maxScore ? Math.round((p.score / p.maxScore) * 100) : 0;
                            return (
                                <div key={p.id} className="flex items-center justify-between text-xs">
                                    <span className="font-semibold text-slate-700">{p.name}</span>
                                    <div className="flex items-center gap-2">
                                        <div className="w-24 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                            <div className={`h-1.5 rounded-full ${pct >= 70 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }}></div>
                                        </div>
                                        <span className="text-slate-500 w-12 text-right">{p.score}/{p.maxScore}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* F-307: ported from the removed Litigation Readiness card —
                Critical Docs / Syntax & Wording / Investigatory / Admin
                Hygiene logic now lives inside Texas Insights. */}
            {pillars.length > 0 && (
                <div className="mt-4" data-testid="insights-pillars">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-slate-500 tracking-wider">COMPLIANCE PILLARS</span>
                        <span className="text-[10px] text-gray-400">Synced with property health</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {pillars.map(({ title, pillar, icon }) => {
                            const isPass = pillar.status === 'PASS';
                            const isWarn = pillar.status === 'WARN';
                            const bg = isPass ? 'bg-green-50 border-green-200' : isWarn ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200';
                            const txt = isPass ? 'text-green-800' : isWarn ? 'text-yellow-800' : 'text-red-800';
                            return (
                                <div key={title} className={`border rounded-lg p-3 ${bg}`}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <div className="flex items-center">
                                            <i className={`fas ${icon} mr-2 ${txt}`}></i>
                                            <h4 className={`text-sm font-semibold ${txt}`}>{title}</h4>
                                        </div>
                                        <span className={`text-xs font-bold ${txt}`}>{pillar.score}/{pillar.max}</span>
                                    </div>
                                    <div className="space-y-1">
                                        {(pillar.items || []).map((item: any, idx: number) => (
                                            <div key={idx} className="flex items-center justify-between text-xs text-gray-600">
                                                <span className="truncate mr-2">{item.type || item.description || 'Check'}</span>
                                                <span>
                                                    {item.status === 'VALID' || item.score === 100
                                                        ? <i className="fas fa-check-circle text-green-500"></i>
                                                        : <i className="fas fa-exclamation-circle text-red-500"></i>}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default {
    RentalRegistrationCard,
    RekeyStatusCard,
    HvacReadinessCard,
    TexasDocumentValidation,
    TexasPropertyInsights,
};
